import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileScan, Loader2, AlertTriangle, CheckCircle2, PackageX, PlusCircle, Camera, X, Scale } from 'lucide-react';
import { toast } from 'sonner';
import { receptionsAPI } from '@/lib/api';
import { PhoneCaptureButton } from '@/components/PhoneCapture';
import ProductPhotoAssistantDialog from '@/components/ProductPhotoAssistantDialog';
import { formatCurrency } from '@/lib/utils';

const STATUS_BADGES = {
    ok: { label: 'Conforme', cls: 'bg-emerald-100 text-emerald-700' },
    qty_diff: { label: 'Qté ≠', cls: 'bg-orange-100 text-orange-700' },
    extra: { label: 'Supplémentaire', cls: 'bg-blue-100 text-blue-700' },
    replacement: { label: 'Remplacement', cls: 'bg-purple-100 text-purple-700' },
    unmatched: { label: 'Non reconnu', cls: 'bg-red-100 text-red-700' },
};

export default function InvoiceReceptionDialog({ open, onOpenChange, order, supplierProducts, onDone }) {
    const CONSOLIDATED_INVOICE_SUPPLIERS = /mg\s*store|asie\s*cara/i;
    const [scope, setScope] = useState('single');
    const [step, setStep] = useState('upload');
    const [file, setFile] = useState(null);
    const [photos, setPhotos] = useState([]);
    const cameraInputRef = useRef(null);
    const [progress, setProgress] = useState('');
    const [percent, setPercent] = useState(0);
    const [invoices, setInvoices] = useState([]);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [recon, setRecon] = useState(null);
    const [edits, setEdits] = useState({});
    const [closeRemaining, setCloseRemaining] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [assistLine, setAssistLine] = useState(null);
    const [extraSps, setExtraSps] = useState([]);
    const pollRef = useRef(null);

    const spForSupplier = [...(supplierProducts || []).filter(p => p.supplier_id === order?.supplier_id), ...extraSps];

    useEffect(() => {
        if (!open) {
            setStep('upload'); setFile(null); setPhotos([]); setInvoices([]); setSelectedInvoice(null);
            setRecon(null); setEdits({}); setCloseRemaining(true); setProgress(''); setPercent(0);
            if (pollRef.current) clearInterval(pollRef.current);
        } else {
            setScope(CONSOLIDATED_INVOICE_SUPPLIERS.test(order?.supplier_name || '') ? 'consolidated' : 'single');
        }
    }, [open, order]);

    const startExtraction = async () => {
        if ((!file && photos.length === 0) || !order) return;
        setStep('extracting');
        setProgress(photos.length > 0 ? 'Envoi des photos…' : 'Envoi du PDF…');
        try {
            const fd = new FormData();
            if (photos.length > 0) {
                photos.forEach((p) => fd.append('photos', p));
            } else {
                fd.append('pdf', file);
            }
            fd.append('supplier_id', order.supplier_id);
            fd.append('site_id', order.site_id);
            const res = await receptionsAPI.importInvoicePreview(fd);
            const jobId = res.data.job_id;
            pollRef.current = setInterval(async () => {
                try {
                    const st = await receptionsAPI.importInvoicePreviewStatus(jobId);
                    setProgress(st.data.progress || '');
                    setPercent(st.data.percent || 0);
                    if (st.data.status === 'done') {
                        clearInterval(pollRef.current);
                        const invs = st.data.result?.invoices || [];
                        if (invs.length === 0) {
                            toast.error('Aucune facture détectée dans ce PDF');
                            setStep('upload');
                        } else if (invs.length === 1) {
                            selectInvoice(invs[0]);
                        } else {
                            setInvoices(invs);
                            setStep('pick');
                        }
                    } else if (st.data.status === 'error') {
                        clearInterval(pollRef.current);
                        toast.error(st.data.error || 'Erreur d’extraction');
                        setStep('upload');
                    }
                } catch { /* transient */ }
            }, 2000);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors de l’envoi du PDF');
            setStep('upload');
        }
    };

    const selectInvoice = async (inv) => {
        setSelectedInvoice(inv);
        setStep('reconciling');
        try {
            const res = await receptionsAPI.invoiceReconcile({
                order_id: order.id,
                invoice_number: inv.invoice_number,
                invoice_date: inv.invoice_date,
                scope,
                items: inv.items,
            });
            setRecon(res.data);
            const initial = {};
            res.data.lines.forEach((l, idx) => {
                initial[idx] = {
                    price: l.invoiced_price || l.order_price || '',
                    override: false,
                    mappedSpId: l.supplier_product_id,
                    wasMapped: false,
                    skip: l.status === 'unmatched',
                    alloc: Object.fromEntries(l.allocations.filter(a => a.order_id).map(a => [a.order_id, a.proposed_qty])),
                };
            });
            setEdits(initial);
            setStep('reconcile');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur de rapprochement');
            setStep('upload');
        }
    };

    const setEdit = (idx, patch) => setEdits(prev => ({ ...prev, [idx]: { ...prev[idx], ...patch } }));

    // Association manuelle d'une ligne non reconnue : re-ventile les quantités
    // par site à partir du restant commandé (remplacement de produit inclus).
    const applyMapping = (idx, l, v) => {
        const patch = { mappedSpId: v, wasMapped: true, skip: false };
        const sp = spForSupplier.find(p => p.id === v);
        const ops = (recon?.ordered_products || []).filter(op => op.remaining_billing > 0);
        const match = ops.find(op => op.supplier_product_id === v)
            || (sp?.industrial_product_id ? ops.find(op => op.industrial_product_id === sp.industrial_product_id) : null);
        if (match) {
            const perOrder = Object.fromEntries((match.per_order || []).map(po => [po.order_id, po.remaining_billing]));
            patch.alloc = Object.fromEntries(l.allocations.filter(a => a.order_id).map(a => [a.order_id, perOrder[a.order_id] ?? 0]));
            if (match.supplier_product_id !== v) {
                patch.replacesSpId = match.supplier_product_id;
                patch.replacesName = match.product_name;
            } else {
                patch.replacesSpId = null;
                patch.replacesName = null;
            }
        }
        setEdit(idx, patch);
    };

    const totalReceived = recon ? recon.lines.reduce((s, l, idx) => {
        const e = edits[idx];
        if (!e || e.skip) return s;
        const q = Object.values(e.alloc).reduce((a, b) => a + (parseFloat(b) || 0), 0);
        return s + q * (parseFloat(e.price) || 0);
    }, 0) : 0;

    const handleCommit = async () => {
        if (!recon || !selectedInvoice) return;
        const unconfirmedVW = recon.lines.some((l, idx) => {
            const e = edits[idx] || {};
            return l.variable_weight && !e.skip && (l.allocations || []).filter(a => a.order_id).length === 1 && !e.vwOk;
        });
        if (unconfirmedVW) {
            toast.warning('Poids variable : acceptez ou corrigez le poids manuscrit lu par l\'IA avant de valider.');
            return;
        }
        setSubmitting(true);
        try {
            const lines = recon.lines.map((l, idx) => ({ l, e: edits[idx] }))
                .filter(({ e }) => e && !e.skip && e.mappedSpId)
                .map(({ l, e }) => ({
                    supplier_product_id: e.mappedSpId,
                    unit_price: parseFloat(e.price) || 0,
                    override_supplier_price: !!e.override,
                    product_ref: l.product_ref || '',
                    description: l.description || '',
                    was_manually_mapped: !!e.wasMapped,
                    replaces_supplier_product_id: (l.status === 'replacement' && e.mappedSpId === l.supplier_product_id) ? l.replaces_supplier_product_id : (e.replacesSpId || null),
                    allocations: Object.entries(e.alloc)
                        .map(([oid, q]) => ({ order_id: oid, quantity: parseFloat(q) || 0 }))
                        .filter(a => a.quantity > 0),
                }))
                .filter(li => li.allocations.length > 0);
            const res = await receptionsAPI.invoiceReceptionCommit({
                order_id: order.id,
                invoice_number: selectedInvoice.invoice_number,
                invoice_date: selectedInvoice.invoice_date,
                close_remaining: closeRemaining,
                force: (recon.duplicates || []).length > 0,
                scope,
                lines,
            });
            toast.success(res.data.message);
            onOpenChange(false);
            onDone?.();
        } catch (e) {
            if (!e.response) {
                toast.warning('Connexion interrompue pendant la validation : la réception a peut-être quand même été enregistrée. Fermez cette fenêtre et vérifiez la commande avant de recommencer.', { duration: 10000 });
            } else {
                toast.error(e.response?.data?.detail || `Erreur lors de la validation (HTTP ${e.response.status})`);
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (!order) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-[96vw] xl:max-w-[1500px] max-h-[92vh] overflow-y-auto" data-testid="invoice-reception-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FileScan className="w-5 h-5 text-orange-500" />
                        Réception via facture — {order.order_number}
                    </DialogTitle>
                </DialogHeader>

                {step === 'upload' && (
                    <div className="space-y-4 py-4">
                        <p className="text-sm text-slate-500">
                            Uploadez la facture PDF du fournisseur <strong>{order.supplier_name}</strong>,
                            ou photographiez une facture papier avec l'appareil photo.
                            Les lignes seront extraites par IA puis rapprochées de la commande
                            {recon?.mode === 'consolidated' ? ' et de ses commandes consolidées' : ''}.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-2">
                            <Input
                                type="file"
                                accept="application/pdf,image/*"
                                className="flex-1"
                                onChange={(e) => {
                                    const f = e.target.files?.[0] || null;
                                    if (f && f.type.startsWith('image/')) {
                                        setPhotos(prev => [...prev, f]);
                                        setFile(null);
                                        e.target.value = '';
                                    } else {
                                        setFile(f);
                                        setPhotos([]);
                                    }
                                }}
                                data-testid="invoice-reception-file-input"
                            />
                            <Button
                                type="button"
                                variant="outline"
                                className="shrink-0 border-orange-300 text-orange-600 hover:bg-orange-50"
                                onClick={() => cameraInputRef.current?.click()}
                                data-testid="invoice-reception-camera-btn"
                            >
                                <Camera className="w-4 h-4 mr-2" />
                                {photos.length > 0 ? 'Ajouter une page' : 'Prendre en photo'}
                            </Button>
                            <input
                                ref={cameraInputRef}
                                type="file"
                                accept="image/*"
                                capture="environment"
                                className="hidden"
                                onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    if (f) {
                                        setPhotos(prev => [...prev, f]);
                                        setFile(null);
                                    }
                                    e.target.value = '';
                                }}
                                data-testid="invoice-reception-camera-input"
                            />
                            <PhoneCaptureButton
                                className="shrink-0"
                                testId="invoice-reception-phone-btn"
                                onPhotos={(fs) => { setPhotos(prev => [...prev, ...fs]); setFile(null); }}
                            />
                        </div>
                        {photos.length > 0 && (
                            <div className="flex flex-wrap gap-2" data-testid="invoice-reception-photos-list">
                                {photos.map((p, i) => (
                                    <div key={i} className="relative border rounded-lg overflow-hidden bg-slate-50">
                                        <img src={URL.createObjectURL(p)} alt={`Page ${i + 1}`} className="h-24 w-20 object-cover" />
                                        <span className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[10px] text-center py-0.5">Page {i + 1}</span>
                                        <button
                                            type="button"
                                            onClick={() => setPhotos(prev => prev.filter((_, j) => j !== i))}
                                            className="absolute top-0.5 right-0.5 bg-black/60 hover:bg-red-600 text-white rounded-full p-0.5"
                                            data-testid={`invoice-reception-photo-remove-${i}`}
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </div>
                                ))}
                                <p className="w-full text-[11px] text-slate-400">
                                    {photos.length} photo(s) — elles seront converties en PDF puis analysées. Ajoutez une page par photo si la facture en compte plusieurs.
                                </p>
                            </div>
                        )}
                        {order.site_name?.startsWith('FE ') && (
                            <div className="border rounded-lg p-3 space-y-2 bg-slate-50">
                                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Cette facture couvre :</div>
                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                    <input type="radio" name="invoice-scope" checked={scope === 'single'} onChange={() => setScope('single')} data-testid="invoice-scope-single" />
                                    <span>Uniquement <strong>{order.site_name}</strong> — une facture par site (cas général : Vodelys…)</span>
                                </label>
                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                    <input type="radio" name="invoice-scope" checked={scope === 'consolidated'} onChange={() => setScope('consolidated')} data-testid="invoice-scope-consolidated" />
                                    <span>Toute la <strong>commande consolidée</strong> de la semaine (MG Store, Asie Caraibes)</span>
                                </label>
                            </div>
                        )}
                        <div className="flex justify-end">
                            <Button onClick={startExtraction} disabled={!file && photos.length === 0} className="btn-primary" data-testid="invoice-reception-analyze-btn">
                                <FileScan className="w-4 h-4 mr-2" /> Analyser la facture
                            </Button>
                        </div>
                    </div>
                )}

                {(step === 'extracting' || step === 'reconciling') && (
                    <div className="flex flex-col items-center gap-3 py-10" data-testid="invoice-reception-progress">
                        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
                        <div className="text-sm text-slate-500">{step === 'reconciling' ? 'Rapprochement avec la commande…' : progress}</div>
                        {step === 'extracting' && <div className="w-64 h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-orange-400 transition-all" style={{ width: `${percent}%` }} />
                        </div>}
                    </div>
                )}

                {step === 'pick' && (
                    <div className="space-y-2 py-2">
                        <p className="text-sm text-slate-500">Plusieurs factures détectées — choisissez celle à rapprocher :</p>
                        {invoices.map((inv, i) => (
                            <button key={i} type="button" onClick={() => selectInvoice(inv)}
                                className="w-full flex items-center justify-between border rounded-lg px-4 py-3 hover:bg-slate-50 text-left"
                                data-testid={`invoice-pick-${i}`}>
                                <div>
                                    <div className="font-semibold">{inv.invoice_number}</div>
                                    <div className="text-xs text-slate-500">{inv.invoice_date} · {inv.items.length} lignes</div>
                                </div>
                                <div className="font-semibold">{formatCurrency(inv.total_ht)}</div>
                            </button>
                        ))}
                    </div>
                )}

                {step === 'reconcile' && recon && (
                    <div className="space-y-4 min-w-0">
                        {(recon.duplicates || []).length > 0 && (
                            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700" data-testid="invoice-duplicate-warning">
                                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                                <div>Cette facture <strong>{selectedInvoice.invoice_number}</strong> est déjà utilisée sur : {recon.duplicates.map(d => d.label).join(', ')}. La validation forcera quand même l'enregistrement.</div>
                            </div>
                        )}
                        <div className="flex flex-wrap items-center gap-2 text-sm">
                            <Badge variant="secondary">{selectedInvoice.invoice_number}</Badge>
                            <Badge variant="secondary">{selectedInvoice.invoice_date}</Badge>
                            <Badge variant="secondary">Total facture : {formatCurrency(selectedInvoice.total_ht)}</Badge>
                            {recon.mode === 'consolidated' && (
                                <Badge className="bg-purple-100 text-purple-700">Consolidée · {recon.orders.length} commandes ({recon.orders.map(o => o.site_name?.replace('FE ', '')).join(', ')})</Badge>
                            )}
                        </div>

                        <div className="border rounded-lg overflow-x-auto max-w-full" style={{ WebkitOverflowScrolling: 'touch' }}>
                            <table className="w-full text-xs">
                                <thead className="bg-slate-50 text-slate-500">
                                    <tr>
                                        <th className="px-2 py-2 text-left">Produit facturé</th>
                                        <th className="px-2 py-2 text-center">Statut</th>
                                        <th className="px-2 py-2 text-right">Commandé (rest.)</th>
                                        <th className="px-2 py-2 text-right">Qté facture</th>
                                        <th className="px-2 py-2 text-right" title="Qté facture − somme répartie sur les sites">Écart</th>
                                        <th className="px-2 py-2 text-right">Prix facture</th>
                                        <th className="px-2 py-2 text-center">MAJ prix réf</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recon.lines.map((l, idx) => {
                                        const e = edits[idx] || {};
                                        const badge = STATUS_BADGES[l.status] || STATUS_BADGES.ok;
                                        const multiAlloc = (l.allocations || []).length > 1;
                                        const allocSum = Object.values(e.alloc || {}).reduce((a, b) => a + (parseFloat(b) || 0), 0);
                                        const ecart = Math.round(((parseFloat(l.invoiced_qty) || 0) - allocSum) * 100) / 100;
                                        const isVW = l.variable_weight || (e.mappedSpId && spForSupplier.find(p => p.id === e.mappedSpId)?.variable_weight);
                                        return (
                                            <React.Fragment key={idx}>
                                                <tr className={`border-t ${e.skip ? 'opacity-40' : ''}`} data-testid={`recon-line-${idx}`}>
                                                    <td className="px-2 py-1.5">
                                                        <div className="font-medium">{l.product_name || l.description}</div>
                                                        <div className="text-slate-400">{l.product_ref} {l.product_name ? `· ${l.description}` : ''}</div>
                                                        {(l.merged_lines || 0) > 1 && (
                                                            <span className="inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-600 border border-blue-200" data-testid={`recon-merged-badge-${idx}`}>
                                                                {l.merged_lines} lignes facture fusionnées
                                                            </span>
                                                        )}
                                                        {l.status === 'unmatched' && !e.skip && (
                                                            <>
                                                            <Select value={e.mappedSpId || ''} onValueChange={(v) => applyMapping(idx, l, v)}>
                                                                <SelectTrigger className="h-7 text-xs mt-1" data-testid={`recon-map-select-${idx}`}>
                                                                    <SelectValue placeholder="Associer à un produit…" />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {spForSupplier.map(p => (
                                                                        <SelectItem key={p.id} value={p.id} className="text-xs">{p.supplier_name_product}</SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                            <Button variant="outline" size="sm" className="h-6 mt-1 text-[11px] text-orange-600 border-orange-300 hover:bg-orange-50 px-2"
                                                                data-testid={`recon-create-product-${idx}`}
                                                                onClick={() => setAssistLine({ idx, line: l })}>
                                                                <PlusCircle className="w-3 h-3 mr-1" />Créer le produit (assistant)
                                                            </Button>
                                                            </>
                                                        )}
                                                        {l.status === 'replacement' && !e.skip && (
                                                            <div className="mt-1 text-[11px] font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-md px-2 py-1 w-fit" data-testid={`recon-replacement-${idx}`}>
                                                                ⇄ Remplace « {l.replaces_product_name} » — la ligne commandée sera clôturée (rupture)
                                                            </div>
                                                        )}
                                                        {e.replacesSpId && !e.skip && (
                                                            <div className="mt-1 text-[11px] font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-md px-2 py-1 w-fit" data-testid={`recon-manual-replacement-${idx}`}>
                                                                ⇄ Remplace « {e.replacesName} » — quantités re-ventilées par site, la ligne commandée sera clôturée (rupture)
                                                            </div>
                                                        )}
                                                        {isVW && !e.skip && (
                                                            <div className="mt-1 space-y-1 w-fit" data-testid={`recon-weight-box-${idx}`}>
                                                                <div className={`flex items-center gap-1.5 border rounded-md px-2 py-1 ${e.vwOk ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                                                                    <Scale className={`w-3.5 h-3.5 shrink-0 ${e.vwOk ? 'text-emerald-600' : 'text-amber-600'}`} />
                                                                    <span className={`text-[11px] font-semibold whitespace-nowrap ${e.vwOk ? 'text-emerald-700' : 'text-amber-700'}`}>Poids livré ({l.billing_unit || 'kg'})</span>
                                                                    {!multiAlloc ? (
                                                                        <Input type="number" step="0.001" inputMode="decimal"
                                                                            className={`h-7 w-24 text-right text-xs bg-white ${e.vwOk ? 'ring-1 ring-emerald-300' : 'ring-1 ring-amber-300'}`}
                                                                            value={e.alloc?.[l.allocations[0]?.order_id] ?? ''}
                                                                            onChange={(ev) => setEdit(idx, { alloc: { ...e.alloc, [l.allocations[0].order_id]: ev.target.value }, vwOk: true })}
                                                                            data-testid={`recon-weight-${idx}`} />
                                                                    ) : (
                                                                        <span className="text-[11px] text-amber-600">saisir le poids par site ↓</span>
                                                                    )}
                                                                </div>
                                                                {!multiAlloc && !e.vwOk && (
                                                                    <div className="flex items-center gap-1.5 text-[11px] text-slate-500 pl-1" data-testid={`recon-vw-confirm-${idx}`}>
                                                                        <span>Manuscrit lu par l'IA : <strong className="text-slate-700">{l.invoiced_qty} {l.billing_unit || 'kg'}</strong></span>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setEdit(idx, { alloc: { ...e.alloc, [l.allocations[0].order_id]: l.invoiced_qty }, vwOk: true })}
                                                                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-600 text-white font-semibold hover:bg-emerald-700"
                                                                            data-testid={`recon-vw-accept-${idx}`}
                                                                        >
                                                                            <CheckCircle2 className="w-3 h-3" /> Accepter
                                                                        </button>
                                                                        <span className="text-slate-400">ou corrigez le poids ci-dessus</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-2 py-1.5 text-center">
                                                        <Badge className={badge.cls}>{badge.label}</Badge>
                                                        {l.price_diff && <Badge className="bg-yellow-100 text-yellow-700 ml-1">Prix ≠</Badge>}
                                                        {l.price_from_reference && <Badge className="bg-blue-100 text-blue-700 ml-1" data-testid={`recon-price-ref-${idx}`}>Tarif PF</Badge>}
                                                    </td>
                                                    <td className="px-2 py-1.5 text-right text-slate-500">
                                                        {l.status === 'extra' || l.status === 'unmatched' ? '—' : `${l.remaining_billing} ${l.billing_unit}`}
                                                    </td>
                                                    <td className="px-2 py-1.5 text-right">
                                                        {!multiAlloc ? (
                                                            <Input type="number" step="0.01" className="h-7 w-20 text-right text-xs ml-auto"
                                                                value={e.alloc?.[l.allocations[0]?.order_id] ?? ''}
                                                                onChange={(ev) => setEdit(idx, { alloc: { ...e.alloc, [l.allocations[0].order_id]: ev.target.value } })}
                                                                disabled={e.skip}
                                                                data-testid={`recon-qty-${idx}`} />
                                                        ) : (
                                                            <span className="font-semibold" data-testid={`recon-invoiced-qty-${idx}`}>{l.invoiced_qty} {l.billing_unit}</span>
                                                        )}
                                                    </td>
                                                    <td className="px-2 py-1.5 text-right">
                                                        {e.skip ? (
                                                            <span className="text-slate-300">—</span>
                                                        ) : ecart === 0 ? (
                                                            <span className="text-emerald-600 font-semibold" data-testid={`recon-ecart-${idx}`}>0</span>
                                                        ) : (
                                                            <span className="text-orange-600 font-bold" title={`Réparti : ${allocSum} — Facturé : ${l.invoiced_qty}`} data-testid={`recon-ecart-${idx}`}>
                                                                {ecart > 0 ? '+' : ''}{ecart}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-2 py-1.5 text-right">
                                                        <div className="flex items-center justify-end gap-1">
                                                            {l.price_diff && <span className="line-through text-slate-400">{formatCurrency(l.order_price)}</span>}
                                                            <Input type="number" step="0.01" className="h-7 w-20 text-right text-xs"
                                                                value={e.price ?? ''}
                                                                onChange={(ev) => setEdit(idx, { price: ev.target.value })}
                                                                disabled={e.skip}
                                                                data-testid={`recon-price-${idx}`} />
                                                        </div>
                                                    </td>
                                                    <td className="px-2 py-1.5 text-center">
                                                        {(l.price_diff || e.wasMapped || (l.order_price != null && e.price !== '' && Math.abs(parseFloat(e.price) - l.order_price) > 0.005)) && (
                                                            <Checkbox checked={!!e.override} onCheckedChange={(v) => setEdit(idx, { override: !!v })} data-testid={`recon-override-${idx}`} />
                                                        )}
                                                    </td>
                                                </tr>
                                                {multiAlloc && !e.skip && (
                                                    <tr className="border-t border-dashed bg-slate-50/60">
                                                        <td colSpan={7} className="px-2 py-1.5">
                                                            <div className="flex flex-wrap items-center gap-3">
                                                                <span className="text-slate-400">Ventilation :</span>
                                                                {l.allocations.map(a => (
                                                                    <label key={a.site_id} className="flex items-center gap-1" title={a.order_id ? `${a.order_number || ''} — commandé rest. : ${a.remaining_billing ?? 0}` : 'Aucune commande pour ce site cette semaine'}>
                                                                        <span className={a.order_id ? 'text-slate-500' : 'text-slate-300'}>
                                                                            {a.site_name?.replace('FE ', '')}
                                                                            {a.order_id && <span className="text-slate-300"> ({a.remaining_billing ?? 0})</span>}
                                                                        </span>
                                                                        <Input type="number" step="0.01" className="h-6 w-16 text-right text-xs disabled:bg-slate-100 disabled:text-slate-300"
                                                                            value={a.order_id ? (e.alloc?.[a.order_id] ?? '') : 0}
                                                                            disabled={!a.order_id}
                                                                            onChange={(ev) => a.order_id && setEdit(idx, { alloc: { ...e.alloc, [a.order_id]: ev.target.value } })}
                                                                            data-testid={`recon-alloc-${idx}-${a.site_id}`} />
                                                                    </label>
                                                                ))}
                                                                {ecart !== 0 && (
                                                                    <span className="text-orange-600 font-semibold" data-testid={`recon-alloc-ecart-${idx}`}>
                                                                        Écart : {ecart > 0 ? '+' : ''}{ecart} {l.billing_unit}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {recon.missing.length > 0 && (
                            <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 space-y-1" data-testid="recon-missing-section">
                                <div className="flex items-center gap-2 text-sm font-semibold text-amber-700">
                                    <PackageX className="w-4 h-4" /> Produits commandés absents de la facture
                                </div>
                                {recon.missing.map((m, i) => (
                                    <div key={i} className="text-xs text-amber-700 flex justify-between">
                                        <span>{m.product_name} <span className="text-amber-500">({m.sites.join(', ')})</span></span>
                                        <span>{m.remaining_billing} {m.billing_unit} non livré(s)</span>
                                    </div>
                                ))}
                                <label className="flex items-center gap-2 pt-1 text-xs text-amber-800">
                                    <Checkbox checked={closeRemaining} onCheckedChange={(v) => setCloseRemaining(!!v)} data-testid="recon-close-remaining-checkbox" />
                                    Clôturer les quantités restantes (la facture fait foi)
                                </label>
                            </div>
                        )}

                        <div className="flex items-center justify-between pt-1">
                            <div className="text-sm">
                                Total réceptionné : <strong>{formatCurrency(totalReceived)}</strong>
                                <span className={`ml-2 text-xs ${Math.abs(totalReceived - (selectedInvoice.total_ht || 0)) > 0.5 ? 'text-orange-600' : 'text-emerald-600'}`}>
                                    (facture : {formatCurrency(selectedInvoice.total_ht)})
                                </span>
                            </div>
                            <div className="flex gap-2">
                                <Button variant="outline" onClick={() => setStep('upload')}>Retour</Button>
                                <Button onClick={handleCommit} disabled={submitting} className="btn-primary" data-testid="recon-commit-btn">
                                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                                    Valider la réception
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </DialogContent>
            <ProductPhotoAssistantDialog
                open={!!assistLine}
                onClose={() => setAssistLine(null)}
                suppliers={order ? [{ id: order.supplier_id, name: order.supplier_name }] : []}
                categories={[]}
                prefillLine={assistLine ? {
                    supplier_id: order.supplier_id,
                    designation: assistLine.line.description || assistLine.line.product_name || '',
                    ref: assistLine.line.product_ref || '',
                    unit_price: assistLine.line.unit_price ?? '',
                    unit: assistLine.line.billing_unit || '',
                } : null}
                onProductCreated={(sp) => {
                    setExtraSps(prev => [...prev, sp]);
                    applyMapping(assistLine.idx, assistLine.line, sp.id);
                    setAssistLine(null);
                    toast.success('Ligne associée au nouveau produit');
                }}
            />
        </Dialog>
    );
}
